import { FC, useCallback } from 'react'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { HeadingXS, ParagraphMD } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useToast } from '@/molecules/Toast'
import { useTranslate } from '@/utils/translate/translate-client'

interface MinimalSharingModalProps {
  onClose: () => void
  open: boolean
  url: string
  movieTitle?: string
  venueName?: string
}

export const MinimalSharingModal: FC<MinimalSharingModalProps> = ({
  onClose,
  open,
  url,
  movieTitle = 'this movie',
  venueName,
}) => {
  const { t } = useTranslate('theatrical-presales')
  const { showToast } = useToast()

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url)
      showToast(t('copiedToClipboard', 'Copied to clipboard!'))
      onClose()
    } catch (error) {
      showToast(t('failedToCopy', 'Failed to copy'))
    }
  }, [url, showToast, t, onClose])

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      transition="slide"
      panelClasses="!max-w-[500px] text-center"
      backgroundScreenColor="black"
      backgroundOverlayClassName="!bg-black/85"
    >
      <div className="relative p-8 pb-6 pt-12">
        <button onClick={onClose} className="absolute right-6 top-6 cursor-pointer rounded-full bg-black/10 p-2">
          <CloseIcon size={16} color="black" />
        </button>

        <FlexColumn className="gap-6">
          <div className="mx-auto mb-4 rounded-full bg-warning-50 p-[1.125rem]">
            <TicketIcon size={32} color="warm-03" />
          </div>

          <FlexColumn className="gap-2">
            <HeadingXS weight="bold" className="text-[1.75rem] leading-tight">
              {venueName
                ? t('inviteOthersToBringMovieToVenue', 'Invite Others to Bring {{movieTitle}} to {{venueName}}', {
                    movieTitle,
                    venueName,
                  })
                : t('inviteOthersToBringMovie', 'Invite Others to Bring {{movieTitle}} to This Theater', {
                    movieTitle,
                  })}
            </HeadingXS>
            <ParagraphMD weight="medium" color="core-gray-700">
              {t(
                'shareThisLinkAndRally',
                'Share this link and rally your community! Help unlock showtimes in your area.',
              )}
            </ParagraphMD>
          </FlexColumn>

          <ParagraphMD weight="medium" color="core-gray-700" className="text-sm">
            {t('copyTheURLBelow', 'Copy the URL below and send it to others via text message, email, or social media.')}
          </ParagraphMD>

          <Button onClick={handleCopyUrl} variant="black" size="md" className="w-full gap-2">
            {t('copyInviteLink', 'Copy Invite Link')}
            <CopyIcon size={20} color="white" />
          </Button>
        </FlexColumn>
      </div>
    </Modal>
  )
}
